import type { AdminUserDto, HouseholdDto } from '@room-mates/shared';
import { type ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuickFilter } from '../../shared/hooks/useQuickFilter.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import { useAuth } from '../../shared/auth/useAuth.js';
import {
  Badge,
  Button,
  Card,
  ConfirmButton,
  EmptyState,
  ErrorAlert,
  FilterInput,
  LoadingBlock,
  PageHeader,
  TextInput,
} from '../../shared/ui/index.js';
import {
  useAdminHouseholds,
  useAdminUsers,
  useDetachHousehold,
  useRenameHousehold,
} from './adminHooks.js';
import { SetPasswordDialog } from './SetPasswordDialog.js';
import { ShareHouseholdDialog } from './ShareHouseholdDialog.js';

export function AdminPage(): ReactElement {
  const t = useTranslation();
  const users = useAdminUsers();
  const households = useAdminHouseholds();

  return (
    <div className="stack stack--loose">
      <PageHeader title={t.admin.title} subtitle={t.admin.subtitle} />
      <ErrorAlert error={users.error ?? households.error} />

      <AccountsSection users={users.data ?? []} isPending={users.isPending} />
      <HouseholdsSection households={households.data ?? []} isPending={households.isPending} />
    </div>
  );
}

function AccountsSection({
  users,
  isPending,
}: {
  users: readonly AdminUserDto[];
  isPending: boolean;
}): ReactElement {
  const t = useTranslation();
  const { user: me, impersonate } = useAuth();
  const navigate = useNavigate();
  const detach = useDetachHousehold();
  const [settingPasswordFor, setSettingPasswordFor] = useState<AdminUserDto | null>(null);
  const [sharingFor, setSharingFor] = useState<AdminUserDto | null>(null);
  const [impersonationError, setImpersonationError] = useState<unknown>(null);

  const filter = useQuickFilter(users, (user) => [user.username, user.householdName, user.role]);

  const viewAs = async (user: AdminUserDto): Promise<void> => {
    setImpersonationError(null);
    try {
      await impersonate(user.id);
      navigate('/sojourns');
    } catch (error) {
      setImpersonationError(error);
    }
  };

  return (
    <Card>
      <div className="stack">
        <h2>{t.admin.accountsTitle}</h2>
        <ErrorAlert error={impersonationError ?? detach.error} />

        {isPending ? <LoadingBlock /> : null}

        {users.length > 0 ? (
          <FilterInput
            label={t.admin.filterAccounts}
            placeholder={t.admin.filterAccountsPlaceholder}
            value={filter.query}
            matchCount={filter.matchCount}
            totalCount={filter.totalCount}
            onChange={filter.setQuery}
          />
        ) : null}

        {filter.isFiltering && filter.matchCount === 0 ? (
          <EmptyState
            title={t.admin.noAccountMatches}
            description={t.admin.noMatchesFor(filter.query)}
          />
        ) : null}

        <ul className="data-list">
          {filter.results.map((user) => (
            <li key={user.id} className="list-row">
              <div className="list-row__main">
                <span className="list-row__title">
                  {user.username}{' '}
                  {user.role === 'admin' ? (
                    <Badge tone="accent">{t.admin.administrator}</Badge>
                  ) : null}
                  {user.id === me?.id ? <Badge>{t.admin.you}</Badge> : null}
                </span>
                <span className="list-row__meta">
                  {user.householdName}
                  {user.householdMemberCount > 1
                    ? ` · ${t.admin.sharedByAccounts(t.count.accounts(user.householdMemberCount))}`
                    : ` · ${t.admin.householdToThemselves}`}
                </span>
              </div>

              <div className="row">
                <Button
                  small
                  variant="secondary"
                  disabled={user.id === me?.id}
                  onClick={() => void viewAs(user)}
                >
                  {t.admin.viewAs}
                </Button>
                <Button small variant="secondary" onClick={() => setSharingFor(user)}>
                  {t.admin.shareData}
                </Button>
                <Button small variant="secondary" onClick={() => setSettingPasswordFor(user)}>
                  {t.admin.setPassword}
                </Button>
                {user.householdMemberCount > 1 ? (
                  <ConfirmButton
                    small
                    label={t.admin.leaveHousehold}
                    confirmLabel={t.admin.leaveTakingNothing}
                    onConfirm={() => detach.mutate(user.id)}
                  />
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {settingPasswordFor !== null ? (
        <SetPasswordDialog user={settingPasswordFor} onClose={() => setSettingPasswordFor(null)} />
      ) : null}
      {sharingFor !== null ? (
        <ShareHouseholdDialog
          user={sharingFor}
          allUsers={users}
          onClose={() => setSharingFor(null)}
        />
      ) : null}
    </Card>
  );
}

function HouseholdsSection({
  households,
  isPending,
}: {
  households: readonly HouseholdDto[];
  isPending: boolean;
}): ReactElement {
  const t = useTranslation();
  const filter = useQuickFilter(households, (household) => [
    household.name,
    ...household.members.map((member) => member.username),
  ]);

  return (
    <Card>
      <div className="stack">
        <div className="stack stack--tight">
          <h2>{t.admin.householdsTitle}</h2>
          <p className="card__meta">{t.admin.householdsSubtitle}</p>
        </div>

        {isPending ? <LoadingBlock /> : null}

        {households.length > 0 ? (
          <FilterInput
            label={t.admin.filterHouseholds}
            placeholder={t.admin.filterHouseholdsPlaceholder}
            value={filter.query}
            matchCount={filter.matchCount}
            totalCount={filter.totalCount}
            onChange={filter.setQuery}
          />
        ) : null}

        {filter.isFiltering && filter.matchCount === 0 ? (
          <EmptyState
            title={t.admin.noHouseholdMatches}
            description={t.admin.noMatchesFor(filter.query)}
          />
        ) : null}

        <ul className="data-list">
          {filter.results.map((household) => (
            <HouseholdRow key={household.id} household={household} />
          ))}
        </ul>
      </div>
    </Card>
  );
}

function HouseholdRow({ household }: { household: HouseholdDto }): ReactElement {
  const t = useTranslation();
  const rename = useRenameHousehold();
  const [draft, setDraft] = useState(household.name);
  const [editing, setEditing] = useState(false);

  return (
    <li className="list-row">
      <div className="list-row__main">
        {editing ? (
          <div className="row">
            <TextInput
              aria-label={t.admin.renameOf(household.name)}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
            <Button
              small
              loading={rename.isPending}
              onClick={() =>
                rename.mutate(
                  { householdId: household.id, input: { name: draft } },
                  { onSuccess: () => setEditing(false) },
                )
              }
            >
              {t.admin.save}
            </Button>
            <Button small variant="ghost" onClick={() => setEditing(false)}>
              {t.common.cancel}
            </Button>
          </div>
        ) : (
          <span className="list-row__title">{household.name}</span>
        )}
        <span className="list-row__meta">
          {household.members.map((member) => member.username).join(', ') || t.admin.noMembers}
        </span>
        <span className="list-row__meta">
          {t.count.houses(household.houseCount)} · {t.count.guests(household.guestCount)} ·{' '}
          {t.count.sojourns(household.sojournCount)}
        </span>
      </div>

      {editing ? null : (
        <Button small variant="secondary" onClick={() => setEditing(true)}>
          {t.admin.rename}
        </Button>
      )}
    </li>
  );
}
